class PremiumProductBenefitsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.slides = [...this.querySelectorAll('[data-benefits-slide]')];
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Math.max(3000, Number(this.dataset.interval) * 1000 || 5000);
    this.autoplayAvailable = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reduceMotion = this.motionQuery.matches;
    this.userStopped = !this.autoplayAvailable || this.reduceMotion;
    this.inView = false;
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.openBenefit = null;
    this.hoverBenefit = null;
    this.desktopQuery.addEventListener('change', () => {
      this.hoverBenefit = null;
      this.syncInlineDetails();
      this.scheduleFlowLayout();
    }, options);
    this.addEventListener('pointerover', (event) => this.handleBenefitHover(event, true), options);
    this.addEventListener('pointerout', (event) => this.handleBenefitHover(event, false), options);
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.openBenefit = null;
        this.hoverBenefit = null;
        this.syncInlineDetails();
      }
    }, options);
    this.addEventListener('click', (event) => this.handleClick(event), options);
    this.addEventListener('keydown', (event) => this.handleKeydown(event), options);
    // Keep a focused control from disappearing during automatic rotation.
    this.addEventListener('focusin', () => this.stopAutoplay(), options);
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-benefits-slide]');
      if (!slide || !this.contains(slide)) return;
      this.stopAutoplay();
      this.setSlide(this.slides.indexOf(slide));
    }, options);
    document.addEventListener('visibilitychange', () => {
      this.syncFlowMotion();
      if (document.hidden) this.clearTimer();
      else this.startTimer();
    }, options);
    this.motionQuery.addEventListener('change', () => {
      this.reduceMotion = this.motionQuery.matches;
      this.syncFlowMotion();
      if (this.reduceMotion) this.stopAutoplay();
      this.updateAutoplayState();
    }, options);
    if ('ResizeObserver' in window) {
      this.flowResizeObserver = new ResizeObserver(() => this.scheduleFlowLayout());
      this.querySelectorAll('.pbf__stage, [data-benefit-card]').forEach((el) => this.flowResizeObserver.observe(el));
    } else {
      window.addEventListener('resize', () => this.scheduleFlowLayout(), options);
    }
    this.setSlide(this.currentIndex);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.dataset.inView = String(this.inView);
        this.syncFlowMotion();
        if (this.inView) this.startTimer();
        else this.clearTimer();
      }, { threshold: 0.05 });
      this.observer.observe(this);
    } else {
      this.inView = true;
      this.dataset.inView = 'true';
      this.syncFlowMotion();
      this.startTimer();
    }
  }

  disconnectedCallback() {
    this.clearTimer();
    this.observer?.disconnect();
    this.flowResizeObserver?.disconnect();
    cancelAnimationFrame(this.flowLayoutFrame);
    this.querySelectorAll('[data-flow-map]').forEach((svg) => svg.pauseAnimations?.());
    this.controller?.abort();
    this.initialized = false;
  }

  handleClick(event) {
    const benefit = event.target.closest('[data-benefit-card]');
    if (benefit && this.contains(benefit)) {
      this.stopAutoplay();
      if (this.desktopQuery.matches) {
        this.openBenefit = this.openBenefit === benefit ? null : benefit;
      }
      this.selectBenefit(benefit, true);
      return;
    }
    const action = event.target.closest('[data-carousel-action]');
    if (!action || !this.contains(action)) return;
    this.stopAutoplay();
    this.setSlide(this.currentIndex + (action.dataset.carouselAction === 'previous' ? -1 : 1), true);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.openBenefit = null;
      this.hoverBenefit = null;
      this.syncInlineDetails();
      return;
    }
    const button = event.target.closest('[data-benefit-card]');
    if (!button || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    const buttons = [...button.closest('[data-benefits-slide]').querySelectorAll('[data-benefit-card]')];
    const current = buttons.indexOf(button);
    let next = current;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    else next += ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -2, ArrowDown: 2 })[event.key];
    const target = buttons[(next + buttons.length) % buttons.length];
    if (!target) return;
    event.preventDefault();
    this.stopAutoplay();
    target.focus({ preventScroll: true });
    if (this.desktopQuery.matches) this.openBenefit = target;
    this.selectBenefit(target, true);
  }

  setSlide(index, announce = false) {
    if (!this.slides.length || !Number.isFinite(index)) return;
    this.currentIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.currentIndex;
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
      if (active) {
        const benefit = slide.querySelector('[data-benefit-card][aria-pressed="true"]') || slide.querySelector('[data-benefit-card]');
        if (benefit) this.selectBenefit(benefit);
      }
    });
    this.openBenefit = null;
    this.hoverBenefit = null;
    this.syncInlineDetails();
    this.scheduleFlowLayout();
    this.syncFlowMotion();
    if (announce) this.announce(this.slides[this.currentIndex].querySelector('.pbf__heading')?.textContent.trim() || 'Product selected');
    this.startTimer();
  }

  selectBenefit(button, announce = false) {
    const slide = button.closest('[data-benefits-slide]');
    const selectedId = button.dataset.benefitPanel;
    slide.querySelectorAll('[data-benefit-card]').forEach((card) => {
      const selected = card === button;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', String(selected));
    });
    slide.querySelectorAll('[data-benefit-detail]').forEach((detail) => {
      const selected = detail.id === selectedId;
      detail.hidden = !selected;
      detail.toggleAttribute('inert', !selected);
      if (selected && announce) this.announce(`${detail.querySelector('.pbf__detail-title').textContent.trim()}. ${detail.querySelector('.pbf__detail-text')?.textContent.trim() || ''}`);
    });
    this.syncInlineDetails();
  }

  handleBenefitHover(event, entering) {
    if (!this.desktopQuery.matches || event.pointerType !== 'mouse') return;
    const card = event.target.closest('[data-benefit-card]');
    if (!card || !this.contains(card) || card.contains(event.relatedTarget)) return;
    if (entering) {
      this.stopAutoplay();
      this.hoverBenefit = card;
    } else if (this.hoverBenefit === card) {
      this.hoverBenefit = null;
    }
    this.syncInlineDetails();
  }

  syncInlineDetails() {
    const desktop = this.desktopQuery.matches;
    const open = this.hoverBenefit || this.openBenefit;
    this.querySelectorAll('[data-benefit-card]').forEach((card) => {
      const inline = card.querySelector('[data-inline-benefit]');
      if (!inline) return;
      const expanded = desktop && card === open && Boolean(inline.textContent.trim()) && !card.closest('[data-benefits-slide]').hidden;
      inline.hidden = !expanded;
      card.classList.toggle('is-expanded', expanded);
      card.setAttribute('aria-expanded', String(desktop ? expanded : card.getAttribute('aria-pressed') === 'true'));
      card.setAttribute('aria-controls', desktop ? inline.id : card.dataset.benefitPanel);
    });
    this.scheduleFlowLayout();
  }

  scheduleFlowLayout() {
    cancelAnimationFrame(this.flowLayoutFrame);
    this.flowLayoutFrame = requestAnimationFrame(() => this.layoutFlowPaths());
  }

  layoutFlowPaths() {
    const slide = this.slides[this.currentIndex];
    const stage = slide?.querySelector('.pbf__stage');
    const svg = stage?.querySelector('[data-flow-map]');
    const media = stage?.querySelector('.pbf__media');
    if (!svg || !media || !stage.clientWidth) return;
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    const cx = width / 2;
    const cy = height / 2;
    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    // Measure only on layout changes, never on animation frames. The SVG handles motion.
    slide.querySelectorAll('[data-benefit-card]').forEach((card) => {
      const number = Number(card.dataset.benefitNumber);
      const path = svg.querySelector(`[data-flow-path="${number}"]`);
      if (!path) return;
      const left = number % 2 === 1;
      const x = card.offsetLeft + (left ? card.offsetWidth - 10 : 10);
      const y = card.offsetTop + ([3, 4].includes(number) ? 0 : card.offsetHeight / 2);
      const dx = cx - x;
      path.setAttribute('d', `M ${x} ${y} C ${x + dx * .55} ${y}, ${cx - dx * .3} ${cy}, ${cx} ${cy}`);
    });
    svg.dataset.ready = 'true';
  }

  syncFlowMotion() {
    const running = this.inView && !this.reduceMotion && !document.hidden;
    this.dataset.flowRunning = String(running);
    this.slides.forEach((slide, index) => {
      const svg = slide.querySelector('[data-flow-map]');
      if (!svg) return;
      if (running && index === this.currentIndex) svg.unpauseAnimations?.();
      else svg.pauseAnimations?.();
    });
  }

  announce(text) {
    const status = this.querySelector('[data-benefit-announcement]');
    if (status) status.textContent = text;
  }

  stopAutoplay() {
    this.userStopped = true;
    this.clearTimer();
    this.updateAutoplayState();
  }

  startTimer() {
    this.clearTimer();
    this.updateAutoplayState();
    if (!this.autoplayAvailable || this.userStopped || this.reduceMotion || !this.inView || document.hidden) return;
    this.timer = window.setTimeout(() => this.setSlide(this.currentIndex + 1), this.interval);
  }

  clearTimer() {
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  updateAutoplayState() {
    const running = this.autoplayAvailable && !this.userStopped && !this.reduceMotion;
    this.dataset.autoplayState = running ? 'running' : 'stopped';

  }
}

if (!customElements.get('premium-product-benefits-carousel')) {
  customElements.define('premium-product-benefits-carousel', PremiumProductBenefitsCarousel);
}
